// face-api.js wrapper. Loads models from the GitHub raw weights URL on demand.
// Exposes load(), captureDescriptor(videoEl), euclideanDistance().

const MODEL_URL = "https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights";

let _loadPromise = null;
let _state = { ready: false, loading: false, error: null };

export function getState() { return { ..._state }; }

function waitForFaceApi(timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    (function check() {
      if (window.faceapi) return resolve(window.faceapi);
      if (Date.now() - start > timeoutMs) return reject(new Error("face-api.js failed to load"));
      setTimeout(check, 150);
    })();
  });
}

export function loadModels(onStatus) {
  if (_loadPromise) return _loadPromise;
  _state.loading = true;
  if (onStatus) onStatus("Loading face-api.js…");
  _loadPromise = (async () => {
    try {
      const faceapi = await waitForFaceApi();
      if (onStatus) onStatus("Loading detector…");
      await faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL);
      if (onStatus) onStatus("Loading landmarks…");
      await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
      if (onStatus) onStatus("Loading recognition…");
      await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
      _state.ready = true;
      _state.loading = false;
      if (onStatus) onStatus("AI Ready");
      return true;
    } catch (e) {
      _state.error = e.message || "Failed to load";
      _state.loading = false;
      if (onStatus) onStatus("Simulation Mode");
      throw e;
    }
  })();
  return _loadPromise;
}

export async function captureDescriptor(videoEl) {
  const faceapi = window.faceapi;
  if (!faceapi || !_state.ready) return null;
  const result = await faceapi
    .detectSingleFace(videoEl, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
    .withFaceLandmarks()
    .withFaceDescriptor();
  if (!result) return null;
  return Array.from(result.descriptor);
}

export function euclideanDistance(a, b) {
  if (!a || !b || a.length !== b.length) return Infinity;
  let s = 0;
  for (let i = 0; i < a.length; i++) {
    const d = a[i] - b[i];
    s += d * d;
  }
  return Math.sqrt(s);
}

// 1:1 verification (self check-in against own samples)
export const FACE_MATCH_THRESHOLD = 0.55;
// 1:N identification (kiosk — matching against ALL employees) must be stricter
// but not so strict that legit users get rejected. Empirically 0.48 is the
// sweet spot for face-api.js 128-dim descriptors (same-person distances
// usually cluster around 0.30-0.42; different-person around 0.55-0.80).
export const KIOSK_MATCH_THRESHOLD = 0.48;
// best match must beat the 2nd-best candidate by at least this margin
export const KIOSK_SECOND_BEST_MARGIN = 0.08;
// "decisive" threshold — if best match is THIS confident, smaller margin is OK
export const KIOSK_DECISIVE_THRESHOLD = 0.38;
// relaxed margin when the best match is already decisive
export const KIOSK_DECISIVE_MARGIN = 0.05;

/**
 * Min Euclidean distance between a live descriptor and a list of reference
 * descriptors (multi-sample enrollment). Returns Infinity if invalid.
 */
export function bestDistance(live, references) {
  if (!live) return Infinity;
  const refs = Array.isArray(references) && references.length
    ? (Array.isArray(references[0]) ? references : [references])
    : [];
  if (!refs.length) return Infinity;
  let best = Infinity;
  for (const r of refs) {
    const d = euclideanDistance(live, r);
    if (d < best) best = d;
  }
  return best;
}

/**
 * Compute per-person distance stats across all enrolled samples.
 * Returns { min, avg, score } — `score = 0.7*min + 0.3*avg` (balances best & worst).
 */
function personScore(live, references) {
  const refs = Array.isArray(references) && references.length
    ? (Array.isArray(references[0]) ? references : [references])
    : [];
  if (!refs.length) return { min: Infinity, avg: Infinity, score: Infinity };
  let min = Infinity;
  let sum = 0;
  for (const r of refs) {
    const d = euclideanDistance(live, r);
    if (d < min) min = d;
    sum += d;
  }
  const avg = sum / refs.length;
  const score = 0.7 * min + 0.3 * avg;
  return { min, avg, score };
}

/**
 * Strict 1:N identification across all enrolled employees.
 *
 * Robust algorithm to avoid mis-identification:
 *  1. For each employee, compute (min, avg, score) across all their face samples.
 *  2. Sort by `score` (min-distance weighted 70% + avg 30%).
 *  3. The best candidate's `min` MUST be ≤ KIOSK_MATCH_THRESHOLD.
 *  4. The score gap between best and 2nd-best MUST be ≥ KIOSK_SECOND_BEST_MARGIN,
 *     OR the best `min` is below KIOSK_DECISIVE_THRESHOLD and gap ≥ KIOSK_DECISIVE_MARGIN.
 *  5. ADDITIONAL guard: best person's `min` must beat 2nd's `min` by at least 0.05
 *     (rules out same-family / sibling lookalikes).
 *
 * Returns { emp, d, secondD, ok, reason }.
 */
export function identifyBest(live, employees) {
  if (!live || !Array.isArray(employees) || employees.length === 0) {
    return { emp: null, d: Infinity, secondD: Infinity, ok: false, reason: "nomatch" };
  }
  const scored = employees
    .map((emp) => ({ emp, ...personScore(live, emp.face_descriptor) }))
    .sort((a, b) => a.score - b.score);
  const best = scored[0];
  const second = scored[1];
  if (!best || best.min > KIOSK_MATCH_THRESHOLD) {
    return { emp: best?.emp || null, d: best?.min ?? Infinity, secondD: second?.min ?? Infinity, ok: false, reason: "nomatch" };
  }
  const scoreGap = (second?.score ?? Infinity) - best.score;
  const isDecisive = best.min <= KIOSK_DECISIVE_THRESHOLD;
  const marginRequired = isDecisive ? KIOSK_DECISIVE_MARGIN : KIOSK_SECOND_BEST_MARGIN;
  if (scoreGap < marginRequired) {
    return { emp: best.emp, d: best.min, secondD: second?.min ?? Infinity, ok: false, reason: "ambiguous" };
  }
  return { emp: best.emp, d: best.min, secondD: second?.min ?? Infinity, ok: true, reason: "ok" };
}
